// src/services/knowledgeBaseService.ts
import apiService from './apiService';
export interface KnowledgeCategory {
  id: string;
  name: string;
  description: string;
  document_count: number;
  icon?: string;
  color?: string;
  parent_id?: string;
  order_index: number;
}

export interface KnowledgeDocument {
  id: string;
  title: string;
  description: string;
  category_id?: string;
  category_name?: string;
  category_color?: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_size_formatted: string;
  file_type: string;
  mime_type: string;
  processing_status: 'pending' | 'processing' | 'completed' | 'error';
  processing_error?: string;
  confidence_score: number;
  equipment_codes?: any[];
  technical_keywords?: string[];
  view_count: number;
  download_count: number;
  tags?: DocumentTag[];
  created_at: string;
  updated_at: string;
  processed_at?: string;
}

export interface DocumentTag {
  id: string;
  name: string;
  color: string;
  auto_assigned: boolean;
}

export interface SearchFilters {
  category_id?: string;
  search?: string;
  file_type?: string;
  date_range?: string;
  min_confidence?: number;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
}

export interface UploadResult {
  success: boolean;
  message: string;
  documentId?: string;
  processing_status?: string;
  error?: string;
}

export interface ProcessingStatus {
  processing_status: 'pending' | 'processing' | 'completed' | 'error';
  processing_error?: string;
  confidence_score?: number;
  processed_at?: string;
}

export interface DocumentStats {
  total_documents: number;
  processed_documents: number;
  pending_documents: number;
  failed_documents: number;
  total_size: number;
  total_size_formatted: string;
  total_views: number;
  total_downloads: number;
  categories: Array<{
    name: string;
    color: string;
    document_count: number;
  }>;
}

export interface SearchResult {
  results: Array<{
    id: string;
    title: string;
    description: string;
    confidence_score: number;
    content_text: string;
  }>;
  query: string;
}

class KnowledgeBaseService {
  private baseUrl = '/knowledge-base';
  private getbaseUrl = '/api/knowledge-base';

async getCategories(): Promise<Category[]> {
  try {
    console.log('📁 Solicitando categorías desde:', `${this.getbaseUrl}/categories`);
    
    const response = await fetch(`${this.getbaseUrl}/categories`);
    console.log('📡 Response status:', response.status);
    console.log('📡 Response ok:', response.ok);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Error response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const text = await response.text();
    console.log('📄 Response text:', text);
    
    const data = JSON.parse(text);
    console.log('📊 Data parsed:', data);
    
    if (!data.success) {
      throw new Error(data.message || 'Error obteniendo categorías');
    }
    
    console.log('✅ Categorías recibidas correctamente:', data.categories.length);
    return data.categories;
    
  } catch (error) {
    console.error('❌ Error en getCategories:', error);
    // ✅ DEVOLVER ARRAY VACÍO EN LUGAR DE HACER THROW
    return [];
  }
}
  /**
   * Crear nueva categoría
   */
  async createCategory(category: Partial<KnowledgeCategory>): Promise<string> {
    try {
      const response = await apiService.post(`${this.getbaseUrl}/categories`, category);
      if (response.success) {
        return response.categoryId;
      }
      throw new Error(response.message || 'Error creando categoría');
    } catch (error) {
      console.error('Error en createCategory:', error);
      throw error;
    }
  }

/**
 * Obtener documentos con filtros
 */
async getDocuments(filters: SearchFilters = {}) {
  try {
    console.log('📄 Obteniendo documentos con filtros:', filters);
    
    const queryParams = new URLSearchParams();
    
    // ✅ DEBE SER 'category' NO 'category_id'
    if (filters.category_id) queryParams.append('category', filters.category_id);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    
    const url = `${this.getbaseUrl}/documents?${queryParams.toString()}`;
    console.log('🔗 URL final:', url);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Error obteniendo documentos');
    }
    
    return {
      documents: data.documents,
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error en getDocuments:', error);
    throw error;
  }
}

/**
 * Obtener estadísticas de la base de conocimientos
 */
async getStats(): Promise<DocumentStats> {
  try {
    console.log('🔗 Obteniendo estadísticas desde:', `${this.baseUrl}/stats`);
    
    const response = await fetch(`${this.baseUrl}/stats`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const text = await response.text();
    const data = JSON.parse(text);
    
    if (!data.success) {
      throw new Error(data.message || 'Error obteniendo estadísticas');
    }
    
    return data.stats;
  } catch (error) {
    console.error('Error en getStats:', error);
    throw error;
  }
}

  /**
   * Obtener detalles de un documento específico
   */
  async getDocument(id: string): Promise<KnowledgeDocument> {
    try {
      const response = await apiService.get(`${this.baseUrl}/documents/${id}`);
      if (response.success) {
        return response.document;
      }
      throw new Error(response.message || 'Documento no encontrado');
    } catch (error) {
      console.error('Error en getDocument:', error);
      throw error;
    }
  }

  /**
   * Subir documento
   */
  async uploadDocument(
    file: File,
    metadata: {
      title?: string;
      description?: string;
      category_id?: string;
      tags?: string[];
      userId?: string; // ✅ AGREGAR USER_ID
      
    } = {},
    onProgress?: (progress: number) => void
  ): Promise<UploadResult> {
    try {
      const formData = new FormData();
      formData.append('document', file);
      
      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.category_id) formData.append('category_id', metadata.category_id);
      if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));
      if (metadata.userId) formData.append('userId', metadata.userId); // ✅ INCLUIR USER_ID

      const response = await fetch(`${this.baseUrl}/documents/upload`, {
        method: 'POST',
        body: formData,
        // No establecer Content-Type para FormData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error en uploadDocument:', error);
      throw error;
    }
  }

  /**
   * Obtener estado de procesamiento de un documento
   */
 async getProcessingStatus(documentId: string): Promise<ProcessingStatus> {
  try {
    console.log('🔍 Obteniendo estado de procesamiento:', documentId);
    
    const url = `${this.getbaseUrl}/documents/${documentId}/processing-status`;
    console.log('🌐 URL:', url);
    
    const response = await fetch(url);
    console.log('📡 Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Error response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const text = await response.text();
    console.log('📄 Response text:', text);
    
    const data = JSON.parse(text);
    console.log('📊 Data parsed:', data);
    
    if (data.success) {
      return {
        processing_status: data.processing_status,
        processing_error: data.processing_error,
        confidence_score: data.confidence_score,
        processed_at: data.processed_at
      };
    }
    throw new Error(data.message || 'Error obteniendo estado');
    
  } catch (error) {
    console.error('❌ Error en getProcessingStatus:', error);
    throw error;
  }
}

  /**
   * Descargar documento
   */
  async downloadDocument(documentId: string, filename?: string): Promise<void> {
    try {
      const response = await fetch(`${this.getbaseUrl}/documents/${documentId}/download`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error en downloadDocument:', error);
      throw error;
    }
  }

  /**
   * Eliminar documento
   */
async deleteDocument(documentId: string, userId?: string): Promise<void> {
  try {
    console.log(`🗑️ Eliminando documento: ${documentId}, Usuario: ${userId}`);
    
    // Agregar userId como query parameter
    const endpoint = `${this.baseUrl}/documents/${documentId}${userId ? `?userId=${userId}` : ''}`;
    const response = await apiService.delete(endpoint);
    
    if (!response.success) {
      throw new Error(response.message || 'Error eliminando documento');
    }
  } catch (error) {
    console.error('Error en deleteDocument:', error);
    throw error;
  }
}

  // 🔍 BÚSQUEDA

  /**
   * Búsqueda semántica
   */
  async semanticSearch(query: string, filters: SearchFilters = {}, limit: number = 10): Promise<SearchResult> {
    try {
      const response = await apiService.post(`${this.baseUrl}/search`, {
        query,
        filters,
        limit
      });

      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Error en búsqueda');
    } catch (error) {
      console.error('Error en semanticSearch:', error);
      throw error;
    }
  }

  /**
   * Buscar documentos similares
   */
  async findSimilarDocuments(documentId: string, limit: number = 5): Promise<KnowledgeDocument[]> {
    try {
      const response = await apiService.get(`${this.baseUrl}/documents/${documentId}/similar?limit=${limit}`);
      if (response.success) {
        return response.documents;
      }
      throw new Error(response.message || 'Error buscando documentos similares');
    } catch (error) {
      console.error('Error en findSimilarDocuments:', error);
      throw error;
    }
  }

  // 🏷️ TAGS

  /**
   * Obtener todos los tags
   */
  async getTags(): Promise<DocumentTag[]> {
    try {
      const response = await apiService.get(`${this.baseUrl}/tags`);
      if (response.success) {
        return response.tags;
      }
      throw new Error(response.message || 'Error obteniendo tags');
    } catch (error) {
      console.error('Error en getTags:', error);
      throw error;
    }
  }

  /**
   * Agregar tags a un documento
   */
  async addTagsToDocument(documentId: string, tagNames: string[]): Promise<void> {
    try {
      const response = await apiService.post(`${this.baseUrl}/documents/${documentId}/tags`, {
        tags: tagNames
      });
      if (!response.success) {
        throw new Error(response.message || 'Error agregando tags');
      }
    } catch (error) {
      console.error('Error en addTagsToDocument:', error);
      throw error;
    }
  }

  /**
   * Remover tags de un documento
   */
  async removeTagsFromDocument(documentId: string, tagIds: string[]): Promise<void> {
    try {
      const response = await apiService.delete(`${this.baseUrl}/documents/${documentId}/tags`, {
        tag_ids: tagIds
      });
      if (!response.success) {
        throw new Error(response.message || 'Error removiendo tags');
      }
    } catch (error) {
      console.error('Error en removeTagsFromDocument:', error);
      throw error;
    }
  }
  // 🔧 UTILIDADES

  /**
   * Validar archivo antes de subir
   */
  validateFile(file: File): string[] {
    const errors: string[] = [];
    
    if (!file) {
      errors.push('No se proporcionó archivo');
      return errors;
    }

    // Validar tamaño (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.push('El archivo es demasiado grande (máximo 50MB)');
    }

    // Validar tipo
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      errors.push(`Tipo de archivo no permitido: ${file.type}`);
    }

    // Validar nombre
    if (file.name.length > 255) {
      errors.push('El nombre del archivo es demasiado largo');
    }

    return errors;
  }

  /**
   * Formatear tamaño de archivo
   */
  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Obtener icono de archivo basado en extensión
   */
  getFileIcon(fileType: string): string {
    switch (fileType.toLowerCase()) {
      case '.pdf': return '📄';
      case '.doc':
      case '.docx': return '📝';
      case '.xls':
      case '.xlsx': return '📊';
      case '.jpg':
      case '.jpeg':
      case '.png':
      case '.gif':
      case '.webp': return '🖼️';
      case '.txt': return '📃';
      default: return '📁';
    }
  }

  /**
   * Obtener color de estado
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'completed': return '#10B981'; // green
      case 'processing': return '#3B82F6'; // blue
      case 'pending': return '#F59E0B'; // yellow
      case 'error': return '#EF4444'; // red
      default: return '#6B7280'; // gray
    }
  }

  /**
   * Monitorear procesamiento de documento
   */
  async monitorDocumentProcessing(
    documentId: string,
    onStatusChange: (status: ProcessingStatus) => void,
    intervalMs: number = 2000
  ): Promise<void> {
    const checkStatus = async () => {
      try {
        const status = await this.getProcessingStatus(documentId);
        onStatusChange(status);

        if (status.processing_status === 'completed' || status.processing_status === 'error') {
          return; // Detener monitoreo
        }

        setTimeout(checkStatus, intervalMs);
      } catch (error) {
        console.error('Error monitoreando procesamiento:', error);
        onStatusChange({
          processing_status: 'error',
          processing_error: 'Error de conexión'
        });
      }
    };

    checkStatus();
  }
}

// Exportar instancia singleton
const knowledgeBaseService = new KnowledgeBaseService();
export default knowledgeBaseService;